import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { isCrmEnabled } from '../../utils/featureFlags';
import { CrmDisabled } from '../crm/CrmDisabled';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/crm/admin');
    }
  }, [isAuthenticated, navigate]);

  // Check if CRM is enabled
  if (!isCrmEnabled()) {
    return <CrmDisabled />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await login(email, password);

      if (!result.success) {
        // Determine error type
        if (result.error.includes('Invalid email or password')) {
          setError('Invalid email or password');
        } else if (result.error.includes('inactive')) {
          setError('Your account is inactive. Please contact support.');
        } else if (result.error.includes('Admin access required')) {
          setError('Admin access required. Please use the admin login.');
        } else {
          setError(result.error || 'Login failed. Please try again.');
        }
        return;
      }

      // Login successful - redirect to admin dashboard
      navigate('/crm/admin');
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border-2 border-[#E5E7EB] p-8 shadow-lg">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#111827] mb-2">CRM Admin Login</h1>
            <p className="text-[#6B7280]">Unisave CRM Admin Portal</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] transition-colors"
                placeholder="admin@unisave.my"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] transition-colors"
                placeholder="••••••••"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in as Admin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
